// src/data/apiData.js

import React from "react";
import { Database, Check, Activity, Zap, Plus, Settings, X, Edit, Cpu, Users } from "lucide-react";

export const SIM_INVENTORY_BASE_URL = "http://192.168.30.33:7076";
export const DEVICE_MANAGEMENT_BASE_URL = "http://192.168.30.33:8080"; 
export const SIM_MANAGEMENT_BASE_URL = "http://192.168.30.33:7078";
export const SMS_WHITELIST_BASE_URL = "http://192.168.30.33:7078";
// --- Configuration Constants (Unchanged) ---
export const methodConfig = {
    GET: { color: "#10B981", bg: "#ECFDF5", icon: <Database size={14} /> },
    POST: { color: "#3B82F6", bg: "#EFF6FF", icon: <Plus size={14} /> },
    PUT: { color: "#F59E0B", bg: "#FFFBEB", icon: <Settings size={14} /> },
    DELETE: { color: "#EF4444", bg: "#FEF2F2", icon: <X size={14} /> },
    PATCH: { color: "#8B5CF6", bg: "#F3E8FF", icon: <Edit size={14} /> },
};

export const difficultyConfig = {
    Basic: { color: "#10B981", icon: <Check size={14} /> },
    Intermediate: { color: "#F59E0B", icon: <Activity size={14} /> },
    Advanced: { color: "#EF4444", icon: <Zap size={14} /> },
};

export const statusCodes = [
    { code: 200, desc: "OK - Request successful", color: "#10B981" },
    { code: 201, desc: "Created - Resource created", color: "#10B981" },
    { code: 400, desc: "Bad Request - Invalid parameters", color: "#F59E0B" },
    { code: 401, desc: "Unauthorized - Invalid API key/Account ID", color: "#EF4444" },
    { code: 404, desc: "Not Found - Resource not found", color: "#EF4444" },
    { code: 500, desc: "Server Error - Internal error", color: "#EF4444" },
];

export const getParameterType = (param) => {
    if (param.header) return "Header";
    if (param.body) return "Request Body";
    if (param.formData) return "Form Data";
    return "Query/Path";
}

export const getHeadersFromEndpoints = (endpoints) => {
    const headers = new Set();
    endpoints.forEach(ep => {
        if (ep.parameters) {
            ep.parameters.filter(p => p.header).forEach(p => headers.add(p.name));
        }
    });
    return Array.from(headers);
}
const simManagementEndpoints = [
    {
        id: 1,
        method: "GET",
        endpoint: "/api/management/company/{id}",
        title: "Get Devices by Company ID",
        description: "Fetch all devices by company ID.",
        category: "Read",
        difficulty: "Basic",
        parameters: [
            { name: "id", type: "Long", required: true, header: false, description: "Company ID (Path Variable)", example: "1" },
            { name: "isSuperAdmin", type: "String", required: false, header: true, description: "Optional: true/false", example: "true" }
        ],
        response: { status: 200, body: `{\n  "httpStatusCode": 200,\n  "status": "Success",\n  "message": "Devices retrieved successfully",\n  "body": [ { "id": 1, "iccid": "8901234567890123540", "simState": "Active", "deviceType": "ASSET_TRACKING" } ]\n}` },
        curlExample: `curl --location '${SIM_MANAGEMENT_BASE_URL}/api/management/company/1' \\\n  --header 'isSuperAdmin: true'`,
    },
    {
        id: 2,
        method: "GET",
        endpoint: "/api/management/account/{id}",
        title: "Get Devices by Account ID",
        description: "Fetch all devices by account ID.",
        category: "Read",
        difficulty: "Basic",
        parameters: [
            { name: "id", type: "Long", required: true, header: false, description: "Account ID (Path Variable)", example: "50" },
            { name: "isSuperAdmin", type: "String", required: false, header: true, description: "Optional: true/false", example: "true" }
        ],
        response: { status: 200, body: `{\n  "httpStatusCode": 200,\n  "status": "Success",\n  "message": "Devices retrieved successfully",\n  "body": [ { "id": 1, "iccid": "8901234567890123540", "simState": "Active", "deviceType": "ASSET_TRACKING" } ]\n}` },
        curlExample: `curl --location '${SIM_MANAGEMENT_BASE_URL}/api/management/account/50' \\\n  --header 'isSuperAdmin: true'`,
    },
    {
        id: 3,
        method: "PUT",
        endpoint: "/api/management/updateStates",
        title: "Update Device States",
        description: "Update the states of multiple devices.",
        category: "Write",
        difficulty: "Intermediate",
        parameters: [
            { name: "body", type: "Array", required: true, body: true, description: "List of ICCIDs with new state", example: `[{"iccid":"8901234567890123540","simState":"Active"}]` }
        ],
        requestBody: `[{"iccid":"8901234567890123540","simState":"Active"}]`,
        response: { status: 200, body: `{\n  "httpStatusCode": 200,\n  "status": "Success",\n  "message": "Device states updated successfully",\n  "body": [ { "iccid": "8901234567890123540", "simState": "Active" } ]\n}` },
        curlExample: `curl --location --request PUT '${SIM_MANAGEMENT_BASE_URL}/api/management/updateStates' \\\n  --header 'Content-Type: application/json' \\\n  --data '[{"iccid":"8901234567890123540","simState":"Active"}]'`,
    },
    {
        id: 4,
        method: "POST",
        endpoint: "/api/management/filter-data",
        title: "Filter Data",
        description: "Filter SIM/device data based on request criteria.",
        category: "Read",
        difficulty: "Intermediate",
        parameters: [
            { name: "body", type: "Object", required: true, body: true, description: "Filter criteria: activationDateFrom, activationDateTo, simType, simState, vendor", example: `{"activationDateFrom":"2025-09-01","activationDateTo":"2025-09-30"}` }
        ],
        requestBody: `{"activationDateFrom":"2025-09-01","activationDateTo":"2025-09-30"}`,
        response: { status: 200, body: `{\n  "httpStatusCode": 200,\n  "status": "Success",\n  "message": "Filtered data retrieved",\n  "body": [ { "id": 1, "iccid": "8901234567890123540", "simState": "Available" } ]\n}` },
        curlExample: `curl --location '${SIM_MANAGEMENT_BASE_URL}/api/management/filter-data' \\\n  --header 'Content-Type: application/json' \\\n  --header 'isSuperAdmin: true' \\\n  --data '{"activationDateFrom":"2025-09-01","activationDateTo":"2025-09-30"}'`,
    },
    {
        id: 5,
        method: "GET",
        endpoint: "/api/management/simByDeviceId/{id}",
        title: "Get SIM by Device ID",
        description: "Fetch SIMs by device ID.",
        category: "Read",
        difficulty: "Basic",
        parameters: [
            { name: "id", type: "Long", required: true, header: false, description: "Device ID (Path Variable)", example: "1" }
        ],
        response: { status: 200, body: `{\n  "httpStatusCode": 200,\n  "status": "Success",\n  "message": "SIMs retrieved successfully",\n  "body": [ { "iccid": "8901234567890123540", "simState": "Available" } ]\n}` },
        curlExample: `curl --location '${SIM_MANAGEMENT_BASE_URL}/api/management/simByDeviceId/1'`,
    },
    {
        id: 6,
        method: "GET",
        endpoint: "/api/management/search",
        title: "Search SIMs",
        description: "Search SIMs by keyword.",
        category: "Read",
        difficulty: "Intermediate",
        parameters: [
            { name: "q", type: "String", required: true, header: false, description: "Search Term", example: "ab" },
            { name: "id", type: "Long", required: true, header: false, description: "Company/Account ID", example: "43" },
            { name: "isSuperAdmin", type: "String", required: false, header: true, description: "Optional: true/false", example: "true" }
        ],
        response: { status: 200, body: `{\n  "httpStatusCode": 200,\n  "status": "Success",\n  "message": "SIMs retrieved successfully",\n  "body": [ { "iccid": "8912340010000000101", "simState": "Available" } ]\n}` },
        curlExample: `curl --location '${SIM_MANAGEMENT_BASE_URL}/api/management/search?q=ab&id=43' \\\n  --header 'isSuperAdmin: true'`,
    },
    {
        id: 7,
        method: "POST",
        endpoint: "/api/management/get/save",
        title: "Save Inventory",
        description: "Save inventory data in bulk.",
        category: "Write",
        difficulty: "Intermediate",
        parameters: [
            { name: "body", type: "Array", required: true, body: true, description: "List of inventory objects", example: `[{"id":1,"batchNumber":"BN001"}]` }
        ],
        requestBody: `[{"id":1,"batchNumber":"BN001"}]`,
        response: { status: 200, body: `{\n  "httpStatusCode": 200,\n  "status": "Success",\n  "message": "Data Saved Successfully",\n  "body": []\n}` },
        curlExample: `curl --location '${SIM_MANAGEMENT_BASE_URL}/api/management/get/save' \\\n  --header 'Content-Type: application/json' \\\n  --data '[{"id":1,"batchNumber":"BN001"}]'`,
    }
];

export const simManagementApiData = {
    key: "SIM_MANAGEMENT",
    title: "SIM Management API Documentation 📘",
    description: "Manage SIM devices, states, filtering, search, and inventory.",
    baseUrl: SIM_MANAGEMENT_BASE_URL,
    prefix: "/api/management",
    icon: <Database size={30} />,
    endpoints: simManagementEndpoints,
};
// --- SIM INVENTORY ENDPOINTS (Your existing data) ---
const simApiEndpoints = [
    // ... (Your 7 SIM endpoints go here, identical to your input)
    {
        id: 1, method: "GET", endpoint: "/sim/inventory/get/all", title: "Get All SIMs", description: "Fetches all SIMs available in the inventory for a given account.", category: "Read", difficulty: "Basic", responseTime: "~100ms",
        parameters: [ { name: "accountId", type: "Long", required: true, header: true, description: "SuperAdmin Account ID", example: "43" } ],
        response: { status: 200, body: `{\n  "success": true,\n  "data": [\n    {\n      "iccid": "89000000000000000001",\n      "simType": "Physical",\n      "status": "Available",\n      "batchNumber": "BN001",\n      "allocatedTo": null\n    },\n    {\n      "iccid": "89000000000000000002",\n      "simType": "Esim",\n      "status": "Allocated",\n      "batchNumber": "BN002",\n      "allocatedTo": "Microsoft"\n    }\n  ],\n  "count": 250\n}`, },
        curlExample: `curl --location '${SIM_INVENTORY_BASE_URL}/sim/inventory/get/all' \\\n  --header 'accountId: 43'`,
    },
    {
        id: 2, method: "GET", endpoint: "/sim/inventory/get/count", title: "Get Data Count", description: "Fetches counts of SIMs and inventory data.", category: "Read", difficulty: "Basic", responseTime: "~80ms",
        parameters: [
            { name: "accountId", type: "Long", required: true, header: true, description: "SuperAdmin Account ID", example: "43" },
            { name: "isSuperAdmin", type: "String", required: false, header: true, description: "Flag to check if the user is a SuperAdmin", example: "true" },
        ],
        response: { status: 200, body: `{\n  "success": true,\n  "data": {\n    "totalSims": 500,\n    "allocatedSims": 150,\n    "availableSims": 350,\n    "physicalSims": 200,\n    "esims": 300\n  }\n}`, },
        curlExample: `curl --location '${SIM_INVENTORY_BASE_URL}/sim/inventory/get/count' \\\n  --header 'accountId: 43' \\\n  --header 'isSuperAdmin: true'`,
    },
    {
        id: 3, method: "GET", endpoint: "/sim/inventory/get/allocated", title: "Get Allocated SIMs", description: "Fetches allocated physical and eSIMs.", category: "Read", difficulty: "Basic", responseTime: "~150ms",
        parameters: [],
        response: { status: 200, body: `{\n  "success": true,\n  "data": [\n    {\n      "iccid": "89000000000000000002",\n      "simType": "Esim",\n      "allocatedTo": "Microsoft",\n      "allocationDate": "2025-09-02T10:00:00Z"\n    }\n  ]\n}`, },
        curlExample: `curl --location '${SIM_INVENTORY_BASE_URL}/sim/inventory/get/allocated'`,
    },
    {
        id: 4, method: "GET", endpoint: "/sim/inventory/get/by/type", title: "Get Inventory by Type", description: "Fetches SIM inventory by SIM type.", category: "Read", difficulty: "Intermediate", responseTime: "~120ms",
        parameters: [ { name: "simType", type: "String", required: true, body: true, description: "Type of SIM to filter by (e.g., 'Physical', 'Esim')", example: "Esim" } ],
        requestBody: `{\n  "simType": "Esim"\n}`,
        response: { status: 200, body: `{\n  "success": true,\n  "data": [\n    {\n      "iccid": "89000000000000000002",\n      "simType": "Esim",\n      "status": "Allocated",\n      "batchNumber": "BN002"\n    }\n  ]\n}`, },
        curlExample: `curl --location --request GET '${SIM_INVENTORY_BASE_URL}/sim/inventory/get/by/type' \\\n  --header 'Content-Type: application/json' \\\n  --data '{"simType":"eSIM"}'`,
    },
    {
        id: 5, method: "POST", endpoint: "/sim/inventory/upload", title: "Upload SIM Inventory", description: "Uploads SIM inventory using an Excel/CSV file.", category: "Write", difficulty: "Intermediate", responseTime: "~500ms",
        parameters: [
            { name: "file", type: "MultipartFile", required: true, formData: true, description: "The Excel/CSV file containing SIM data", example: "/path/to/file.xlsx" },
            { name: "simType", type: "String", required: true, formData: true, description: "The type of SIMs being uploaded (e.g., 'eSIM')", example: "eSIM" },
        ],
        response: { status: 200, body: `{\n  "success": true,\n  "message": "SIM inventory uploaded successfully",\n  "uploadedCount": 150\n}`, },
        curlExample: `curl --location '${SIM_INVENTORY_BASE_URL}/sim/inventory/upload' \\\n  --form 'file=@"/path/to/file"' \\\n  --form 'simType="eSIM"'`,
    },
    {
        id: 6, method: "PUT", endpoint: "/sim/inventory/allocate", title: "Allocate SIMs", description: "Allocates SIMs to a user/device.", category: "Write", difficulty: "Advanced", responseTime: "~300ms",
        parameters: [
            { name: "batchNumber", type: "String", required: true, body: true, description: "Batch number of the SIMs to allocate", example: "BN001" },
            { name: "simType", type: "String", required: true, body: true, description: "Type of SIM to allocate (e.g., 'Esim')", example: "Esim" },
            { name: "simCount", type: "integer", required: true, body: true, description: "Number of SIMs to allocate", example: "1" },
            { name: "targetAccountId", type: "Long", required: true, body: true, description: "Account ID to allocate the SIMs to", example: "40" },
            { name: "targetAccountName", type: "String", required: true, body: true, description: "Account Name to allocate the SIMs to", example: "Microsoft" },
        ],
        requestBody: `{\n  "batchNumber": "BN001",\n  "simType": "Esim",\n  "simCount": 1,\n  "targetAccountId": 40,\n  "targetAccountName": "Microsoft"\n}`,
        response: { status: 200, body: `{\n  "success": true,\n  "message": "1 SIM(s) from batch BN001 allocated to Microsoft"\n}`, },
        curlExample: `curl --location --request PUT '${SIM_INVENTORY_BASE_URL}/sim/inventory/allocate' \\\n  --header 'Content-Type: application/json' \\\n  --data '{\n    "batchNumber": "BN001",\n    "simType": "Esim",\n    "simCount": 1,\n    "targetAccountId": 40,\n    "targetAccountName": "Microsoft"\n}'`,
    },
    {
        id: 7, method: "POST", endpoint: "/sim/inventory/get/excel/data", title: "Export SIM Inventory to Excel", description: "Exports SIM inventory to an Excel file and downloads it.", category: "Read", difficulty: "Intermediate", responseTime: "~400ms",
        parameters: [
            { name: "accountId", type: "Long", required: true, header: true, description: "Account ID for filtering the export", example: "43" },
            { name: "batchNumber", type: "String", required: false, body: true, description: "Filter by batch number", example: "BN001" },
            { name: "allocatedAccountName", type: "String", required: false, body: true, description: "Filter by the name of the allocated account", example: "usha" },
            { name: "allocationDate", type: "String", required: false, body: true, description: "Filter by allocation date (YYYY-MM-DD HH:MM:SS)", example: "2025-09-02 19:26:50" },
        ],
        requestBody: `{\n  "batchNumber": "BN001",\n  "allocatedAccountName": "usha",\n  "allocationDate": "2025-09-02 19:26:50"\n}`,
        response: { status: 200, body: `{\n  "success": true,\n  "message": "File export initiated. Download should start shortly."\n}`, },
        curlExample: `curl --location '${SIM_INVENTORY_BASE_URL}/sim/inventory/get/excel/data' \\\n  --header 'accountId: 43' \\\n  --header 'Content-Type: application/json' \\\n  --data '{\n    "batchNumber": "BN001",\n    "allocatedAccountName": "usha",\n    "allocationDate": "2025-09-02 19:26:50"\n}'`,
    }
];

// ====================================================================
// A. SIM INVENTORY API OBJECT
// ====================================================================
const simApiData = {
    key: "SIM_INVENTORY",
    title: "SIM Inventory API Documentation 📘",
    description: "Manage physical and eSIM card inventory across accounts.",
    baseUrl: SIM_INVENTORY_BASE_URL,
    prefix: "/sim/inventory",
    icon: <Database size={30} />,
    endpoints: simApiEndpoints,
};



const smsWhitelistEndpoints = [
    {
        id: 201,
        method: "POST",
        endpoint: "/create",
        title: "Create SMS Whitelist",
        description: "Create a new SMS whitelist group with numbers and regions.",
        category: "Write",
        difficulty: "Intermediate",
        responseTime: "~250ms",
        parameters: [
            { name: "accountId", type: "Long", required: true, header: true, description: "Account ID of the requester", example: "43" },
        ],
        requestBody: `{
  "name": "Promo_SMS_Whitelist",
  "description": "Whitelist group for promotional SMS campaigns",
  "status": "ACTIVE",
  "region": ["India","APAC"],
  "accountName": "Apple",
  "whitelistNumbers": [
    {
      "whitelistNumberType1": "INCOMING",
      "whitelistNumber1": "9876543210",
      "whitelistNumberType2": "INCOMING",
      "whitelistNumber2": "8901234567890123456",
      "whitelistNumberType3": "OUTGOING",
      "whitelistNumber3": "404100000000001",
      "whitelistNumberType4": "OUTGOING",
      "whitelistNumber4": "123456"
    }
  ]
}`,
        response: {
            status: 200,
            body: `{
  "id": 24,
  "name": "Promo_SMS_Whitelist",
  "description": "Whitelist group for promotional SMS campaigns",
  "requestId": 41206,
  "status": "ACTIVE",
  "accountName": "Apple",
  "submittedAt": "2025-09-26 06:31:34",
  "region": ["India","APAC"],
  "whitelistNumberResponse": [ ... ]
}`
        },
        curlExample: `curl --location '${SMS_WHITELIST_BASE_URL}/api/sms/whitelist/create' \\
  --header 'accountId: 43' \\
  --header 'Content-Type: application/json' \\
  --data '{...}'`,
    },
    {
        id: 202,
        method: "GET",
        endpoint: "/get",
        title: "Get SMS Whitelist Data",
        description: "Fetch all SMS whitelist groups for the account.",
        category: "Read",
        difficulty: "Basic",
        responseTime: "~200ms",
        parameters: [
            { name: "accountId", type: "Long", required: true, header: true, description: "Account ID of the requester", example: "43" },
            { name: "isSuperAdmin", type: "String", required: false, header: true, description: "Optional flag to fetch all data", example: "true" },
        ],
        response: {
            status: 200,
            body: `[{
  "id": 7,
  "name": "Watsoo Plan",
  "description": "sedgfhjn,k",
  "requestId": 83752,
  "status": "Accepted",
  "accountName": null,
  "submittedAt": "2025-09-12 15:56:30",
  "region": ["Andhra Pradesh"],
  "whitelistNumberResponse": [ ... ]
}]`
        },
        curlExample: `curl --location '${SMS_WHITELIST_BASE_URL}/api/sms/whitelist/get' \\
  --header 'accountId: 43'`,
    },
    {
        id: 203,
        method: "PATCH",
        endpoint: "/update/status",
        title: "Update Whitelist Status",
        description: "Update the status of an existing SMS whitelist request.",
        category: "Write",
        difficulty: "Intermediate",
        responseTime: "~150ms",
        parameters: [
            { name: "accountId", type: "Long", required: true, header: true, description: "Account ID of the requester", example: "43" }
        ],
        requestBody: `{
  "requestId": 65814,
  "status": "Done"
}`,
        response: {
            status: 200,
            body: `{
  "message": "Status updated successfully"
}`
        },
        curlExample: `curl --location --request PATCH '${SMS_WHITELIST_BASE_URL}/api/sms/whitelist/update/status' \\
  --header 'Content-Type: application/json' \\
  --header 'accountId: 43' \\
  --data '{ "requestId": 65814, "status": "Done" }'`,
    },
    {
        id: 204,
        method: "GET",
        endpoint: "/whitelisted/sims",
        title: "Fetch Whitelisted SIMs",
        description: "Retrieve SIMs that are part of the SMS whitelist with optional filters.",
        category: "Read",
        difficulty: "Intermediate",
        responseTime: "~250ms",
        parameters: [
            { name: "iv-user", type: "String", required: true, header: true, description: "User performing the request", example: "developer.74678@watsoo.com" },
            { name: "customer-id", type: "Long", required: true, header: true, description: "Customer ID", example: "74678" },
            { name: "msisdn", type: "String", required: false, description: "Filter by MSISDN", example: "9876543210" },
            { name: "simNo", type: "String", required: false, description: "Filter by SIM Number", example: "8901234567890123456" },
            { name: "euiccId", type: "String", required: false, description: "Filter by EUICC ID", example: "12345678" },
            { name: "basketId", type: "Integer", required: false, description: "Filter by basket ID", example: "1" },
            { name: "pageNo", type: "Integer", required: false, description: "Page number for pagination", example: "1" },
            { name: "pageSize", type: "Integer", required: false, description: "Number of results per page", example: "25" },
        ],
        response: {
            status: 200,
            body: `{
  "data": {
    "total": null,
    "smsWTDetailsVO": []
  }
}`
        },
        curlExample: `curl --location '${SMS_WHITELIST_BASE_URL}/api/sms/whitelist/whitelisted/sims?msisdn=9876543210&pageNo=1&pageSize=25' \\
  --header 'iv-user: developer.74678@watsoo.com' \\
  --header 'customer-id: 74678'`,
    }
];
export const smsWhitelistApiData = {
    key: "SMS_WHITELIST",
    title: "SMS Whitelist API Documentation 📩",
    description: "Manage SMS whitelist groups and whitelisted SIMs.",
    baseUrl: SMS_WHITELIST_BASE_URL,
    prefix: "/api/sms/whitelist",
    icon: <Database size={30} />,
    endpoints: smsWhitelistEndpoints,
};



const orderManagementEndpoints = [
    {
        id: 1,
        method: "POST",
        endpoint: "/api/order/add",
        title: "Add an Order",
        description: "Create a new order in the system.",
        category: "Write",
        difficulty: "Basic",
        parameters: [
            { name: "body", type: "Object", required: true, body: true, description: "Order details JSON", example: `{
  "orderId": 0,
  "accountName": "Acme Corp",
  "accountType": "Corporate",
  "orderNumber": 123456,
  "orderDate": "2025-09-29T07:49:04.497Z",
  "orderStatus": "Completed",
  "orderDescription": "Order for 10 SIM cards",
  "simType": "4G",
  "numberOfSims": "10",
  "accountId": 456,
  "rejectionReason": "",
  "primaryProfile": "Profile A",
  "secondaryProfile": "Profile B",
  "cost": 1500,
  "email": "contact@acmecorp.com"
}` }
        ],
        requestBody: `{"orderId":0,"accountName":"Acme Corp","accountType":"Corporate","orderNumber":123456,"orderDate":"2025-09-29T07:49:04.497Z","orderStatus":"Completed","orderDescription":"Order for 10 SIM cards","simType":"4G","numberOfSims":"10","accountId":456,"rejectionReason":"","primaryProfile":"Profile A","secondaryProfile":"Profile B","cost":1500,"email":"contact@acmecorp.com"}`,
        response: { status: 200, body: `{\n "httpStatusCode":200,\n "status":"Success",\n "message":"Order added successfully.",\n "body": { "orderId":123,"accountName":"Acme Corp","orderStatus":"Completed"}\n}` },
        curlExample: `curl --location --request POST '${DEVICE_MANAGEMENT_BASE_URL}/api/order/add' \\
  --header 'Content-Type: application/json' \\
  --data '{ "orderId":0,"accountName":"Acme Corp","accountType":"Corporate","orderNumber":123456,"orderDate":"2025-09-29T07:49:04.497Z","orderStatus":"Completed","orderDescription":"Order for 10 SIM cards","simType":"4G","numberOfSims":"10","accountId":456,"rejectionReason":"","primaryProfile":"Profile A","secondaryProfile":"Profile B","cost":1500,"email":"contact@acmecorp.com"}'`,
    },
    {
        id: 2,
        method: "POST",
        endpoint: "/api/order/addBatch",
        title: "Add Batch Orders",
        description: "Create multiple orders in a single request.",
        category: "Write",
        difficulty: "Intermediate",
        parameters: [
            { name: "body", type: "Array", required: true, body: true, description: "Array of order objects", example: `[{"orderId":0,"accountName":"Acme Corp","orderNumber":123456},{"orderId":0,"accountName":"Beta Ltd","orderNumber":123457}]` }
        ],
        requestBody: `[{"orderId":0,"accountName":"Acme Corp","accountType":"Corporate","orderNumber":123456,"orderDate":"2025-09-29T07:49:04.500Z","orderStatus":"Completed","simType":"4G","numberOfSims":"10","accountId":456,"cost":1500,"email":"contact@acmecorp.com"},{"orderId":0,"accountName":"Beta Ltd","accountType":"SMB","orderNumber":123457,"orderDate":"2025-09-29T07:50:00.000Z","orderStatus":"Pending","simType":"5G","numberOfSims":"5","accountId":457,"cost":800,"email":"sales@betaltd.com"}]`,
        response: { status: 200, body: `{\n "httpStatusCode":200,\n "status":"Success",\n "message":"Batch orders added successfully.",\n "body":[{"orderId":123,"accountName":"Acme Corp"},{"orderId":124,"accountName":"Beta Ltd"}]\n}` },
        curlExample: `curl --location --request POST '${DEVICE_MANAGEMENT_BASE_URL}/api/order/addBatch' \\
  --header 'Content-Type: application/json' \\
  --data '[{"orderId":0,"accountName":"Acme Corp","orderNumber":123456},{"orderId":0,"accountName":"Beta Ltd","orderNumber":123457}]'`,
    },
    {
        id: 3,
        method: "PUT",
        endpoint: "/api/order/update",
        title: "Update an Order",
        description: "Modify an existing order.",
        category: "Write",
        difficulty: "Intermediate",
        parameters: [
            { name: "body", type: "Object", required: true, body: true, description: "Order object with updated fields", example: `{"orderId":123,"orderDescription":"Updated description for order"}` }
        ],
        requestBody: `{"orderId":123,"accountName":"Acme Corp","accountType":"Corporate","orderNumber":123456,"orderDate":"2025-09-29T07:49:04.495Z","orderStatus":"Completed","orderDescription":"Updated description for order","simType":"4G","numberOfSims":"10","accountId":456,"primaryProfile":"Profile A","secondaryProfile":"Profile B","cost":1500,"email":"contact@acmecorp.com"}`,
        response: { status: 200, body: `{\n "httpStatusCode":200,\n "status":"Success",\n "message":"Order updated successfully.",\n "body":123\n}` },
        curlExample: `curl --location --request PUT '${DEVICE_MANAGEMENT_BASE_URL}/api/order/update' \\
  --header 'Content-Type: application/json' \\
  --data '{"orderId":123,"orderDescription":"Updated description for order"}'`,
    },
    {
        id: 4,
        method: "GET",
        endpoint: "/api/order/getById/{id}",
        title: "Get Order by ID",
        description: "Retrieve a specific order by its ID.",
        category: "Read",
        difficulty: "Basic",
        parameters: [
            { name: "id", type: "Long", required: true, header: false, description: "Order ID (Path Variable)", example: "123" },
            { name: "isSuperAdmin", type: "Boolean", required: false, header: true, description: "Optional: true/false", example: "true" }
        ],
        response: { status: 200, body: `{\n "httpStatusCode":200,\n "status":"Success",\n "message":"Order retrieved successfully.",\n "body":[{"orderId":123,"accountName":"Acme Corp"}]\n}` },
        curlExample: `curl --location '${DEVICE_MANAGEMENT_BASE_URL}/api/order/getById/123' \\
  --header 'isSuperAdmin: true'`,
    },
    {
        id: 5,
        method: "GET",
        endpoint: "/api/order/download/{orderNumber}",
        title: "Download Order by Order Number",
        description: "Download order details in file format using order number.",
        category: "Read",
        difficulty: "Intermediate",
        parameters: [
            { name: "orderNumber", type: "Long", required: true, header: false, description: "Order Number (Path Variable)", example: "123456" }
        ],
        response: { status: 200, body: `File (PDF/Excel/JSON) returned as response for given order number.` },
        curlExample: `curl --location '${DEVICE_MANAGEMENT_BASE_URL}/api/order/download/123456'`,
    }
];

export const orderManagementApiData = {
    key: "ORDER_MANAGEMENT",
    title: "Order Management API Documentation 📦",
    description: "Manage customer orders: add, batch insert, update, retrieve, and download.",
    baseUrl: DEVICE_MANAGEMENT_BASE_URL,
    prefix: "/api/order",
    icon: <Cpu size={30} />,
    endpoints: orderManagementEndpoints,
};


// ====================================================================
// SUPPORT TICKETS API OBJECT
// ====================================================================

const supportTicketsEndpoints = [
    {
        id: 1,
        method: "POST",
        endpoint: "/tickets/create",
        title: "Create a Support Ticket",
        description: "Create a new support ticket with issue details and conversation history.",
        category: "Write",
        difficulty: "Basic",
        parameters: [
            { name: "accountId", type: "Integer", required: true, header: true, description: "Account ID of requester", example: "456" },
            { name: "body", type: "Object", required: true, body: true, description: "Support ticket details JSON", example: `{"name":"John Doe","issueType":"Login Issue","status":"Open"}` }
        ],
        requestBody: `{
  "name": "John Doe",
  "email": "john.doe@example.com",
  "issueType": "Login Issue",
  "description": "Cannot log in to the platform with my credentials.",
  "pageUrl": "https://example.com/login",
  "screenshot": "base64EncodedImageString==",
  "status": "Open",
  "accountId": 456,
  "date": "2025-09-29T07:49:04.482Z",
  "ticketId": 0,
  "conversationHistory": [
    {
      "id": 0,
      "sender": "John Doe",
      "message": "I’m unable to log in since yesterday.",
      "time": "2025-09-29T07:49:04.482Z",
      "supportTicket": "string"
    }
  ]
}`,
        response: { status: 200, body: `{\n "id":123,"name":"John Doe","email":"john.doe@example.com","issueType":"Login Issue","status":"Open","ticketId":789,"conversations":[{"id":1,"sender":"John Doe","message":"I’m unable to log in since yesterday."},{"id":2,"sender":"Support Agent","message":"We are looking into this issue."}]}` },
        curlExample: `curl --location '${DEVICE_MANAGEMENT_BASE_URL}/tickets/create' \\
  --header 'accountId: 456' \\
  --header 'Content-Type: application/json' \\
  --data '{ "name":"John Doe","email":"john.doe@example.com","issueType":"Login Issue","description":"Cannot log in","status":"Open","accountId":456,"ticketId":0 }'`,
    },
    {
        id: 2,
        method: "PUT",
        endpoint: "/tickets/update/status",
        title: "Update Ticket Status",
        description: "Update the status of a support ticket and add a resolution message.",
        category: "Write",
        difficulty: "Basic",
        parameters: [
            { name: "body", type: "Object", required: true, body: true, description: "Ticket update details", example: `{"ticketId":789,"status":"Closed","message":"Resolved"}` }
        ],
        requestBody: `{
  "sender": "Support Agent",
  "message": "Your issue has been resolved.",
  "timestamp": "2025-09-29T07:49:04.479Z",
  "status": "Closed",
  "ticketId": 789
}`,
        response: { status: 200, body: `"Ticket status updated successfully."` },
        curlExample: `curl --location --request PUT '${DEVICE_MANAGEMENT_BASE_URL}/tickets/update/status' \\
  --header 'Content-Type: application/json' \\
  --data '{"sender":"Support Agent","message":"Your issue has been resolved.","status":"Closed","ticketId":789}'`,
    },
    {
        id: 3,
        method: "GET",
        endpoint: "/tickets/getByTicketId/{ticketId}",
        title: "Get Ticket by Ticket ID (Path Variable)",
        description: "Retrieve a specific support ticket using its Ticket ID.",
        category: "Read",
        difficulty: "Basic",
        parameters: [
            { name: "ticketId", type: "Integer", required: true, header: false, description: "Ticket ID (Path Variable)", example: "789" }
        ],
        response: { status: 200, body: `{"id":123,"name":"John Doe","issueType":"Login Issue","status":"Open","ticketId":789,"conversations":[{"id":1,"sender":"John Doe"},{"id":2,"sender":"Support Agent"}]}` },
        curlExample: `curl --location '${DEVICE_MANAGEMENT_BASE_URL}/tickets/getByTicketId/789'`,
    },
    {
        id: 4,
        method: "GET",
        endpoint: "/tickets/getAll",
        title: "Get All Tickets",
        description: "Retrieve all tickets for an account. Supports optional super admin access.",
        category: "Read",
        difficulty: "Intermediate",
        parameters: [
            { name: "accountId", type: "Integer", required: true, header: true, description: "Account ID", example: "456" },
            { name: "isSuperAdmin", type: "Boolean", required: false, header: true, description: "Super admin flag", example: "true" }
        ],
        response: { status: 200, body: `[{"id":123,"name":"John Doe","issueType":"Login Issue","status":"Open"},{"id":124,"name":"Jane Smith","issueType":"Payment Issue","status":"In Progress"}]` },
        curlExample: `curl --location '${DEVICE_MANAGEMENT_BASE_URL}/tickets/getAll' \\
  --header 'accountId: 456' \\
  --header 'isSuperAdmin: true'`,
    },
    {
        id: 5,
        method: "GET",
        endpoint: "/tickets/get/byTicketId",
        title: "Get Ticket by Ticket ID (Query Parameter)",
        description: "Retrieve a ticket by passing the Ticket ID as a query parameter.",
        category: "Read",
        difficulty: "Basic",
        parameters: [
            { name: "ticketId", type: "Integer", required: true, header: false, description: "Ticket ID (Query Param)", example: "789" }
        ],
        response: { status: 200, body: `[{"id":123,"name":"John Doe","issueType":"Login Issue","status":"Open","ticketId":789}]` },
        curlExample: `curl --location '${DEVICE_MANAGEMENT_BASE_URL}/tickets/get/byTicketId?ticketId=789'`,
    },
    {
        id: 6,
        method: "DELETE",
        endpoint: "/tickets/delete/{ticketId}",
        title: "Delete Ticket by Ticket ID",
        description: "Delete a support ticket by its Ticket ID.",
        category: "Write",
        difficulty: "Basic",
        parameters: [
            { name: "ticketId", type: "Integer", required: true, header: false, description: "Ticket ID (Path Variable)", example: "789" }
        ],
        response: { status: 200, body: `"Ticket deleted successfully."` },
        curlExample: `curl --location --request DELETE '${DEVICE_MANAGEMENT_BASE_URL}/tickets/delete/789'`,
    }
];

export const supportTicketsApiData = {
    key: "SUPPORT_TICKETS",
    title: "Support Tickets API Documentation 🎫",
    description: "Manage customer support tickets: create, update, fetch, and delete tickets.",
    baseUrl: DEVICE_MANAGEMENT_BASE_URL,
    prefix: "/tickets",
    icon: <Cpu size={30} />,
    endpoints: supportTicketsEndpoints,
};
const dashboardEndpoints = [
    {
        id: 1,
        method: "GET",
        endpoint: "/dashboard/{id}",
        title: "Get Dashboard Data",
        description: "Fetches dashboard data for a user. If `isSuperAdmin` header is true, fetches admin-level dashboard data; otherwise, fetches data for the given user ID.",
        category: "Read",
        difficulty: "Basic",
        parameters: [
            { name: "id", type: "Long", required: true, header: false, description: "User ID whose dashboard is to be retrieved", example: "101" },
            { name: "isSuperAdmin", type: "Boolean", required: false, header: true, description: "Flag indicating if requester is super admin (default: false)", example: "true" }
        ],
        response: {
            status: 200,
            body: `{
  "status": 200,
  "message": "Success",
  "description": "SIMs retrieved successfully",
  "timestamp": "2025-09-26T14:30:00",
  "data": {
    "expiry": {
      "expiryTomorrow": 5,
      "expiryWeek": 15,
      "expiryMonth": 40,
      "expiredInLast30Days": 3,
      "expiredMoreThan30DaysAgo": 2
    },
    "activation": {
      "todayActivated": 10,
      "activatedWeek": 50,
      "activatedMonth": 200
    },
    "simAnalysis": {
      "totalSim": 1000,
      "activated": 800,
      "available": 150,
      "suspended": 30,
      "totalExpired": 20
    },
    "profileCounts": [
      { "availableProfiles": "G1", "count": 120 },
      { "availableProfiles": "G2", "count": 80 }
    ]
  }
}`
        },
        curlExample: `curl --location '${SIM_MANAGEMENT_BASE_URL}/dashboard/101' \\
  --header 'Content-Type: application/json' \\
  --header 'isSuperAdmin: true'`,
    }
];

export const dashboardApiData = {
    key: "DASHBOARD",
    title: "Dashboard API Documentation 📊",
    description: "Fetch user or admin dashboard data including SIM expiry, activation, and profile counts.",
    baseUrl: SIM_MANAGEMENT_BASE_URL,
    prefix: "/dashboard",
    icon: <Database size={30} />,
    endpoints: dashboardEndpoints,
};


export const API_USER_BASE_URL = "http://192.168.30.33:8081";

const apiUserEndpoints = [
  {
    id: 1,
    method: "GET",
    endpoint: "/api/v1/get_api_user",
    title: "Get All API Users",
    description: "Fetch all the available ApiUsers.",
    category: "ApiUser",
    difficulty: "Basic",
    parameters: [],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ ... ] }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/api/v1/get_api_user' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  },
  {
    id: 2,
    method: "GET",
    endpoint: "/api/user/get_usernames/byAccount?accountName={accountName}",
    title: "Get API Users By Account",
    description: "Fetch all ApiUsers for a particular account.",
    category: "ApiUser",
    difficulty: "Basic",
    parameters: [
      { name: "accountName", type: "String", required: true, header: false, description: "Account name to filter users", example: "myAccount" }
    ],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ ... ] }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/api/user/get_usernames/byAccount?accountName=myAccount' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  },
  {
    id: 3,
    method: "PUT",
    endpoint: "/api/v1/update",
    title: "Update ApiUser",
    description: "Update fields of an ApiUser.",
    category: "ApiUser",
    difficulty: "Intermediate",
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" },
      { name: "Content-Type", type: "String", required: true, description: "Request format", example: "application/json" }
    ],
    requestBody: `{
  "companyId": 19,
  "id": 1,
  "userId": 9,
  "userName": "Rapidsoft User1",
  "email": "Rapidsoft13@example.com",
  "phone": 9876543331,
  "account_name": "Rapidsoft",
  "accountId": "account_220",
  "roleId": "role_api_user",
  "status": "Inactive"
}`,
    response: {
      status: 200,
      body: `{ "httpStatusCode": 200 }`
    },
    curlExample: `curl --location --request PUT '${API_USER_BASE_URL}/api/v1/update' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer <your_access_token>' \\
  --data-raw '{ "companyId": 19, "id":1, "userId": 9, ... }'`,
  },
  {
    id: 4,
    method: "POST",
    endpoint: "/api/v1/create_User_Api",
    title: "Create ApiUser",
    description: "Create a new ApiUser based on request criteria.",
    category: "ApiUser",
    difficulty: "Intermediate",
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" },
      { name: "Content-Type", type: "String", required: true, description: "Request format", example: "application/json" }
    ],
    requestBody: `{
  "companyId": 14,
  "userName": "NKC User",
  "email": "NKCuser3@gmail.com",
  "phone": 9871143221,
  "account_name": "Rapidsoft",
  "accountId": "account_1100",
  "roleName": "MGT_Role",
  "status": "Active"
}`,
    response: {
      status: 200,
      body: `{ "httpStatusCode": 200 }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/api/v1/create_User_Api' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer <your_access_token>' \\
  --data-raw '{ "companyId": 14, "userName": "NKC User", ... }'`,
  },
  {
    id: 5,
    method: "DELETE",
    endpoint: "/api/v1/deleteuser/{id}",
    title: "Delete ApiUser",
    description: "Delete an ApiUser by ID.",
    category: "ApiUser",
    difficulty: "Basic",
    parameters: [
      { name: "id", type: "Long", required: true, header: false, description: "ApiUser ID", example: "7" }
    ],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ "httpStatusCode": 200 }`
    },
    curlExample: `curl --location --request DELETE '${API_USER_BASE_URL}/api/v1/deleteuser/7' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  },

  // ------------------- AssignedApis -------------------
  {
    id: 6,
    method: "GET",
    endpoint: "/apiUser/AssignedApis",
    title: "Get All Assigned APIs",
    description: "Returns a list of all assigned APIs.",
    category: "AssignedApis",
    difficulty: "Basic",
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ ... ] }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/apiUser/AssignedApis' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  },
  {
    id: 7,
    method: "GET",
    endpoint: "/apiUser/AssignedApisByName/{name}",
    title: "Get Assigned APIs By Name",
    description: "Fetch all AssignedApis assigned to a particular ApiUser.",
    category: "AssignedApis",
    difficulty: "Basic",
    parameters: [
      { name: "name", type: "String", required: true, header: false, description: "ApiUser name", example: "Nitesh" }
    ],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ ... ] }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/apiUser/AssignedApisByName/Nitesh' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  },
  {
    id: 8,
    method: "PUT",
    endpoint: "/apiUser/UpdateAssignedApis/byName/{assignedTo}",
    title: "Update Assigned API",
    description: "Update assigned APIs for a given ApiUser (assignedTo).",
    category: "AssignedApis",
    difficulty: "Intermediate",
    parameters: [
      { name: "assignedTo", type: "String", required: true, header: false, description: "ApiUser name", example: "Nitesh" }
    ],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" },
      { name: "Content-Type", type: "String", required: true, description: "Request format", example: "application/json" }
    ],
    requestBody: `{ "role": "Admin", "account": "Youtube", "environment": "Testing", ... }`,
    response: {
      status: 200,
      body: `{ "httpStatusCode": 200 }`
    },
    curlExample: `curl --location --request PUT '${API_USER_BASE_URL}/apiUser/UpdateAssignedApis/byName/Nitesh' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer <your_access_token>' \\
  --data '{ "role": "Admin", "account": "Youtube", ... }'`,
  },
  {
    id: 9,
    method: "POST",
    endpoint: "/apiUser/AssignedApis",
    title: "Create Assigned API",
    description: "Assigns a new API to an ApiUser.",
    category: "AssignedApis",
    difficulty: "Intermediate",
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" },
      { name: "Content-Type", type: "String", required: true, description: "Request format", example: "application/json" }
    ],
    requestBody: `{ "role": "Superadmin", "account": "Account_100", "assignedTo": "harry", ... }`,
    response: {
      status: 200,
      body: `{ "httpStatusCode": 200 }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/apiUser/AssignedApis' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer <your_access_token>' \\
  --data '{ "role": "Superadmin", "account": "Account_100", ... }'`,
  },

  // ------------------- Permissions -------------------
  {
    id: 10,
    method: "GET",
    endpoint: "/api/userApi/allpermission",
    title: "Get All Permissions",
    description: "Fetch all available API permissions.",
    category: "Permission",
    difficulty: "Basic",
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ ... ] }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/api/userApi/allpermission' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  },
  {
    id: 11,
    method: "GET",
    endpoint: "/api/userApi/category",
    title: "Get Permissions Grouped by Category",
    description: "Fetch API permissions grouped by category.",
    category: "Permission",
    difficulty: "Basic",
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ "User Management": {...}, "Order Management": {...} }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/api/userApi/category' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  },
  {
    id: 12,
    method: "PUT",
    endpoint: "/api/userApi/updatepermission/{id}",
    title: "Update Permission",
    description: "Update an existing API permission.",
    category: "Permission",
    difficulty: "Intermediate",
    parameters: [
      { name: "id", type: "Long", required: true, header: false, description: "Permission ID", example: "1" }
    ],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" },
      { name: "Content-Type", type: "String", required: true, description: "Request format", example: "application/json" }
    ],
    requestBody: `{ "apiName": "get_users", "endpoint": "/api/users", ... }`,
    response: {
      status: 200,
      body: `{ "httpStatusCode": 200 }`
    },
    curlExample: `curl --location --request PUT '${API_USER_BASE_URL}/api/userApi/updatepermission/1' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer <your_access_token>' \\
  --data '{ "apiName": "get_users", "endpoint": "/api/users", ... }'`,
  },
  {
    id: 13,
    method: "POST",
    endpoint: "/api/userApi/permission?companyId={companyId}",
    title: "Create Permission",
    description: "Create a new API permission for a company/account.",
    category: "Permission",
    difficulty: "Intermediate",
    parameters: [
      { name: "companyId", type: "Long", required: true, header: false, description: "Company ID", example: "12" }
    ],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" },
      { name: "Content-Type", type: "String", required: true, description: "Request format", example: "application/json" }
    ],
    requestBody: `{ "apiName": "Get_user", "endpoint": "/api/users", ... }`,
    response: {
      status: 200,
      body: `{ "httpStatusCode": 200 }`
    },
    curlExample: `curl --location '${API_USER_BASE_URL}/api/userApi/permission?companyId=12' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer <your_access_token>' \\
  --data '{ "apiName": "Get_user", "endpoint": "/api/users", ... }'`,
  },
  {
    id: 14,
    method: "DELETE",
    endpoint: "/api/userApi/deletepermission?id={id}",
    title: "Delete Permission",
    description: "Delete an API permission by ID.",
    category: "Permission",
    difficulty: "Basic",
    parameters: [
      { name: "id", type: "Long", required: true, header: false, description: "Permission ID", example: "4" }
    ],
    headers: [
      { name: "Authorization", type: "String", required: true, description: "Bearer access token", example: "Bearer <your_access_token>" }
    ],
    response: {
      status: 200,
      body: `{ "status": "success", "message": "Permission deleted successfully" }`
    },
    curlExample: `curl --location --request DELETE '${API_USER_BASE_URL}/api/userApi/deletepermission?id=4' \\
  --header 'Authorization: Bearer <your_access_token>'`,
  }
];

export const apiUserApiData = {
  key: "APIUSER",
  title: "ApiUser Controller API Documentation 👤",
  description: "Manage API Users, Assigned APIs, and Permissions in one place.",
  baseUrl: API_USER_BASE_URL,
  prefix: "/api/v1",
  icon: <Users size={30} />,
  endpoints: apiUserEndpoints,
};
export const apiMap = {
    [simApiData.key]: simApiData, 
    [simManagementApiData.key]: simManagementApiData,
    [smsWhitelistApiData.key]: smsWhitelistApiData,
    [orderManagementApiData.key]: orderManagementApiData, // Order Management Controller API
    [supportTicketsApiData.key]: supportTicketsApiData,   // Support Tickets Controller API
    [dashboardApiData.key]: dashboardApiData,             // Dashboard Controller API
    [apiUserApiData.key]: apiUserApiData,                 // ApiUser Controller API (includes AssignedApis + Permission)
  };


// Set the default key for the initial state in ApiDoc.js
export const SIM_INVENTORY_KEY = simApiData.key;